import React, { Fragment } from 'react';

export function getCarmaxOfferFaqContent() {
  return [
    {
      question: 'Do I need to buy a car from CarMax to sell one?',
      answer: 'No, you can simply sell your car to CarMax without buying another car.',
    },
    {
      question: 'What do I need to sell my car at CarMax?',
      answer:
        'Requirements can vary from state to state, but there are a few items you&apos;ll need no matter where you are (1) Your car&apos;s title or payoff information. All titleholders should be present. (2) Valid and current registrations (3) Valid state-issued photo ID for all title holders (4) All keys and remotes.',
    },
    {
      question: 'Will the CarMax Offer price change when they see my car?',
      answer:
        'CarMax will verify the answers you provided online are accurate when you arrive in the store.  If all of the answers are accurate, you will receive the exact amount of your online offer.',
    },
    {
      question: 'How do I get paid for my car?',
      answer:
        'You will leave with payment in hand the same day you sell CarMax your car. CarMax will pay with a bank draft, which is a type of check where payment is guaranteed to be available by the issuing bank. A bank draft must be deposited and cannot be cashed. Before attempting to draw on the bank draft, always check with your bank to see if they have a specific hold policy on deposits.',
    },
  ];
}

export const ESTIMATED_OFFER_ENTRY = {
  question: 'What is the difference between an estimated offer and an online offer?',
  answer: [
    'If you see a single offer price, then you have received an online offer from CarMax. If the answers you provided online are accurate, then you will receive this amount when you sell your car.',
    'If you see a range of prices, this means you have received an estimated offer based on the information provided online. You receive an estimated offer when CarMax needs additional information in order to accurately appraise your vehicle. CarMax will need to appraise your car in-person to provide a firm offer. In some cases, your actual offer may fall outside the range shown.',
  ],
};

export function getCarmaxEstimatedOfferFaqContent() {
  return [
    {
      question: 'Do I need to buy a car from CarMax to sell one?',
      answer: 'No, you can simply sell your car to CarMax without buying another car.',
    },
    {
      question: 'What do I need to sell my car at CarMax?',
      answer:
        'Requirements can vary from state to state, but there are a few items you&apos;ll need no matter where you are (1) Your car&apos;s title or payoff information. All titleholders should be present. (2) Valid and current registrations (3) Valid state-issued photo ID for all title holders (4) All keys and remotes.',
    },
    {
      question: 'Will the CarMax Offer price change when they see my car?',
      answer: [
        'CarMax will verify the answers you provided online are accurate when you arrive in the store.  If all of the answers are accurate, you will receive the exact amount of your online offer.',
        'If you see a range of prices, this means you have received an estimated offer based on the information provided online. You receive an estimated offer when CarMax needs additional information in order to accurately appraise your vehicle. CarMax will need to appraise your car in-person to provide a firm offer. In some cases, your actual offer may fall outside the range shown.',
      ],
    },
    ESTIMATED_OFFER_ENTRY,
    {
      question: 'How do I get paid for my car?',
      answer:
        'You will leave with payment in hand the same day you sell CarMax your car. CarMax will pay with a bank draft, which is a type of check where payment is guaranteed to be available by the issuing bank. A bank draft must be deposited and cannot be cashed. Before attempting to draw on the bank draft, always check with your bank to see if they have a specific hold policy on deposits.',
    },
  ];
}

export const WHY_GET_OFFER = {
  question: 'Why should I get an offer with my appraisal?',
  answer:
    "An appraisal is just an estimated value of your car but not a guarantee for purchase. In comparison, a firm trade-in offer is a real offer to buy your vehicle, good at your nearest CarMax location for seven days. If you're selling privately, you can use this offer as a baseline for your asking price. Or, if you're comfortable with the offer, you can accept it and redeem it at your local CarMax.",
};

export const WHAT_IS_RELATIONSHIP_BETWEEN_EDMUNDS_AND_CARMAX = {
  question: 'What is the relationship between Edmunds and CarMax?',
  answer: [
    'Since June 2021, Edmunds has been a wholly owned subsidiary of CarMax, Inc.',
    'To learn more about the relationship between Edmunds and CarMax, see our <a href="/about/visitor-agreement.html">Visitor Agreement</a>.',
  ],
};

function getCommonPartnerFaqContent() {
  return [
    {
      question: 'Why do you need my VIN or license plate?',
      answer:
        'Your VIN or license plate is used to determine if you are eligible to receive an instant offer for your car from CarMax with your appraisal report.  Not all vehicles qualify for an online offer based on CarMax&apos;s eligibility criteria which includes a vehicle history check. No additional personal information is required.',
    },
    WHY_GET_OFFER,
    WHAT_IS_RELATIONSHIP_BETWEEN_EDMUNDS_AND_CARMAX,
  ];
}

export const IS_CARMAX_OFFER_REAL = {
  question: 'Is the CarMax offer a real offer?',
  answer:
    'Yes! As long as the information provided online is accurate, and you visit your local CarMax before the offer expires, you will receive the online offer amount shown here.',
};

export const getHowCarmaxDetermineOffer = ({ appendSlot = null } = {}) => ({
  question: 'How does CarMax determine my offer?',
  answer: (
    <Fragment>
      <span>
        CarMax reviews current market conditions as well as your car&apos;s specific features and specs, mileage,
        condition, and vehicle history report. When redeeming your offer, they will examine your car&apos;s condition
        inside and out to make sure it matches the information provided online. They may also test drive your car and
        check to see if the brakes and turn signals are working (among other things). If repairs are needed, they may
        adjust your offer accordingly. In regard to cleanliness, the long-term maintenance and care of your car is
        what&apos;s most important in determining your offer.
      </span>{' '}
      {appendSlot}
    </Fragment>
  ),
  omitContentFragment: true,
});

export const DOES_CARMAX_BUY_LEASED_CARS = {
  question: 'Does CarMax buy leased cars?',
  answer:
    'Yes! In most cases, you can sell your leased car in almost the same way as any other financed car. CarMax will appraise the car, then contact the leasing company for a payoff quote and process any equity you might have. Please note that CarMax cannot purchase a leased vehicle from certain companies. For more details, click <a href ="https://www.carmax.com/sell-my-car#faq-lease" target="_blank">here</a>.',
};

export const IF_I_OWE_MORE = {
  question: 'What if I owe more on my car than the amount of my offer?',
  answer: `If your payoff amount is more than the offer for your car, the difference is called "negative equity". In some cases, the negative equity can be included in your financing when you buy a CarMax car. If not, CarMax will calculate the difference between your pay-off and their offer to you and you can pay CarMax directly. If the amount you owe is less than $250, they will accept a personal check. CarMax stores accept cashier's checks, certified checks, certified funds, cash, and debit cards. CarMax Car Buying Centers (locations that only buy cars) can accept cashier's checks, certified checks, and certified funds. CarMax’s offers are good for seven days, giving you time to get any necessary funds to settle your transaction.`,
};

export const CAN_I_NEGOTIATE = {
  question: 'Can I negotiate the online offer?',
  answer: 'No. All offers received on Edmunds.com are firm.',
};

export const WILL_CARMAX_PICK_UP = {
  question: 'Will CarMax pick up my vehicle from my home?',
  answer:
    'CarMax currently only offers pickup service in select markets. Check with your local CarMax to confirm. You can also redeem an offer at any CarMax store.',
};

export const DO_I_NEED_AN_APPOINTMENT = {
  question: 'Do I need an appointment to sell my car?',
  answer:
    "No, you don't need an appointment to sell your car. However, an appointment will help get you in and out in as little time as possible.",
};

export const WHAT_IS_EDMUNDS_TMV = {
  question: 'What is Edmunds True Market Value®?',
  answer: [
    'Edmunds True Market Value&reg; (TMV&reg;), also known as Edmunds Suggested Price, is a pricing system that helps you determine the average transaction price &mdash; or what others are paying &mdash; for new or used vehicles in your area so that you can begin your negotiations with a fair price in mind. For customers appraising their vehicles for sale, Edmunds TMV&reg; helps you understand how much you might expect to receive when you sell.',
    'We determine those prices based on a variety of factors, including actual transaction data from dealers. CarMax is one of the dealers from which Edmunds obtains transaction data.',
    'Since June 2021, Edmunds has been a wholly owned subsidiary of CarMax, Inc. However, other than the incorporation of CarMax&apos;s transaction data by Edmunds into TMV&reg; calculations, CarMax does not participate in or otherwise influence the manner in which Edmunds computes those values.',
  ],
};

export function getAppraiserFaqContent(commonFaqContent = []) {
  return [
    ...getCommonPartnerFaqContent(),
    ...getCarmaxEstimatedOfferFaqContent(),
    ...commonFaqContent,
    IS_CARMAX_OFFER_REAL,
    getHowCarmaxDetermineOffer(),
    DOES_CARMAX_BUY_LEASED_CARS,
    IF_I_OWE_MORE,
    CAN_I_NEGOTIATE,
    WILL_CARMAX_PICK_UP,
    DO_I_NEED_AN_APPOINTMENT,
  ];
}
